import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { ROUTES } from "@/app/routes";
import type { components } from "@/core/api/schema";
import { useSession } from "@/core/auth/session";
import { formatCount, formatCountOf, formatDayLong, formatPaise, zoneLabel } from "@/core/format/format";
import { Card, CardHeader } from "@/ui/card";
import { cn } from "@/ui/cn";
import { Skeleton } from "@/ui/spinner";
import { ErrorState, PageHeader } from "@/ui/states";

import { useAdminOverview } from "./overview.queries";

import { BusinessNumbers } from "./BusinessNumbers";

type Overview = components["schemas"]["AdminOverview"];

/** Meal statuses in the order a day moves through them, each with its chart colour. */
const MEAL_STATUS_DISPLAY: { status: string; label: string; swatch: string }[] = [
  { status: "SCHEDULED", label: "Scheduled", swatch: "bg-chart-1" },
  { status: "DISPATCHED", label: "On the way", swatch: "bg-chart-2" },
  { status: "DELIVERED", label: "Delivered", swatch: "bg-chart-3" },
  { status: "RECEIVED", label: "Received", swatch: "bg-chart-4" },
  { status: "PAUSED", label: "Paused", swatch: "bg-chart-5" },
  { status: "AUTO_PAUSED", label: "Auto-paused", swatch: "bg-chart-5" },
  { status: "DISPUTED", label: "Disputed", swatch: "bg-chart-6" },
  { status: "MISSED_BY_BRAND", label: "Missed by kitchen", swatch: "bg-chart-6" },
];

function greeting(name: string | null | undefined): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const first = name?.trim().split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}

function Stat({ label, value, detail }: { label: string; value: ReactNode; detail?: ReactNode }) {
  return (
    <Card className="flex min-w-0 flex-col gap-2 p-4 sm:gap-2.5 sm:p-5">
      <span className="text-[13px] font-semibold text-muted">{label}</span>
      <span className="tabular text-2xl leading-none font-extrabold tracking-tight sm:text-[30px]">
        {value}
      </span>
      {detail && <span className="text-[13px] text-ink-2">{detail}</span>}
    </Card>
  );
}

function AttentionRow({
  count,
  title,
  detail,
  to,
  action,
  tone,
}: {
  count: number;
  title: string;
  detail: string;
  to: string;
  action: string;
  tone: "danger" | "warn" | "info";
}) {
  const tint = {
    danger: "bg-danger-soft text-danger",
    warn: "bg-warn-soft text-warn",
    info: "bg-info-soft text-info",
  }[tone];
  return (
    <Link
      to={to}
      className="flex items-center gap-3.5 border-t border-border-subtle px-1 py-3.5 text-ink no-underline first:border-t-0 hover:bg-surface-muted"
    >
      <span
        className={cn(
          "tabular flex size-10 shrink-0 items-center justify-center rounded-xl text-[15px] font-extrabold",
          tint,
        )}
      >
        {formatCount(count)}
      </span>
      <span className="flex min-w-0 grow flex-col gap-0.5">
        <span className="text-[15px] font-semibold">{title}</span>
        <span className="text-[13px] text-muted">{detail}</span>
      </span>
      <span className="flex items-center gap-1 text-sm font-bold whitespace-nowrap text-link">
        {action}
        <ChevronRight className="size-4" aria-hidden="true" />
      </span>
    </Link>
  );
}

function OverviewSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading the overview">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-[126px] rounded-2xl" />
        ))}
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(380px,1fr))] gap-4">
        <Skeleton className="h-64 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    </div>
  );
}

function OverviewContent({ data }: { data: Overview }) {
  const meals = MEAL_STATUS_DISPLAY.map((m) => ({
    ...m,
    count: data.mealsToday.byStatus[m.status] ?? 0,
  })).filter((m) => m.count > 0);
  const mealTotal = meals.reduce((sum, m) => sum + m.count, 0);
  const tasks = [
    data.openDisputes > 0 && {
      count: data.openDisputes,
      title: "Open disputes",
      detail: "Customers say a meal never arrived",
      to: ROUTES.disputes,
      action: "Review",
      tone: "danger" as const,
    },
    data.payoutsDue.brands > 0 && {
      count: data.payoutsDue.brands,
      title: data.payoutsDue.brands === 1 ? "Brand to pay" : "Brands to pay",
      detail: `${formatPaise(data.payoutsDue.amountPaise)} due across ${formatCountOf(data.payoutsDue.tranches, "tranche")}`,
      to: ROUTES.payouts,
      action: "Pay",
      tone: "warn" as const,
    },
    data.pendingDeletions > 0 && {
      count: data.pendingDeletions,
      title: "Account deletions waiting",
      detail: "Check what blocks them",
      to: `${ROUTES.users}?status=DELETION_REQUESTED`,
      action: "Check",
      tone: "info" as const,
    },
  ].filter((t) => t !== false);

  return (
    <>
      <section aria-label="Key numbers" className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <Stat
          label="Active subscriptions"
          value={formatCount(data.subscriptions.active)}
          detail={`${formatCount(data.subscriptions.scheduled)} starting soon`}
        />
        <Stat
          label="Meals today"
          value={formatCount(data.mealsToday.toServe)}
          detail="Still to serve today"
        />
        <Stat
          label="Collected this month"
          value={formatPaise(data.thisMonth.collectedPaise)}
          detail="Online payments, tax included"
        />
        <Stat
          label="Payouts due"
          value={formatPaise(data.payoutsDue.amountPaise)}
          detail={formatCountOf(data.payoutsDue.brands, "brand")}
        />
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card aria-labelledby="attention">
          <CardHeader
            id="attention"
            title="Needs attention"
            aside={tasks.length ? formatCountOf(tasks.length, "item") : undefined}
          />
          {tasks.length === 0 ? (
            <p className="rounded-xl bg-primary-soft p-4 text-sm text-primary-soft-ink">
              All clear. Nothing is waiting for you.
            </p>
          ) : (
            <div className="flex flex-col">
              {tasks.map((task) => (
                <AttentionRow key={task.title} {...task} />
              ))}
            </div>
          )}
        </Card>

        <Card aria-labelledby="meals" className="flex flex-col gap-4">
          <CardHeader
            id="meals"
            title="Today's meals"
            aside={formatCountOf(mealTotal, "meal")}
            className="mb-0"
          />
          {mealTotal === 0 ? (
            <p className="text-sm text-muted">No meals today.</p>
          ) : (
            <>
              <div
                role="img"
                aria-label="Today's meals by status"
                className="flex h-3.5 gap-0.5 overflow-hidden rounded-full"
              >
                {meals.map((m) => (
                  <span key={m.status} className={m.swatch} style={{ flexGrow: m.count }} />
                ))}
              </div>
              <ul className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                {meals.map((m) => (
                  <li key={m.status} className="flex items-center gap-2.5">
                    <span className={cn("size-2.5 rounded-[3px]", m.swatch)} aria-hidden="true" />
                    <span className="grow text-sm text-ink-2">{m.label}</span>
                    <span className="tabular text-sm font-bold">{formatCount(m.count)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>

      <Card aria-labelledby="money">
        <CardHeader
          id="money"
          title="This month's money"
          aside={
            <Link to={ROUTES.reconciliation} className="font-bold no-underline">
              Open reconciliation
            </Link>
          }
        />
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
          {[
            { label: "Collected", value: data.thisMonth.collectedPaise, note: "Paid online, tax included" },
            { label: "Commission", value: data.thisMonth.platformRevenuePaise, note: "Brocoly's revenue" },
            {
              label: "Promo cost",
              value: data.thisMonth.platformPromoCostPaise,
              note: "Platform promos and free plans",
            },
          ].map((m) => (
            <div key={m.label} className="flex flex-col gap-1.5 rounded-xl bg-surface-muted p-4">
              <span className="text-[13px] font-semibold text-muted">{m.label}</span>
              <span className="tabular text-[22px] font-extrabold">{formatPaise(m.value)}</span>
              <span className="text-xs text-muted">{m.note}</span>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

export function OverviewPage() {
  const { me } = useSession();
  const overview = useAdminOverview();
  const data = overview.data;

  return (
    <>
      <PageHeader
        title={greeting(me?.name)}
        subtitle={data ? `${formatDayLong(data.date)} · times in ${zoneLabel(data.timezone)}` : "Overview"}
      />
      {overview.isPending ? (
        <OverviewSkeleton />
      ) : overview.isError ? (
        <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
      ) : (
        <OverviewContent data={data!} />
      )}
      <BusinessNumbers />
    </>
  );
}
